import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useEffect, useSyncExternalStore } from "react";
import { vibrieren } from "../plattform/geraet";
import type { Zustandsanzeige } from "./jagdzustand";

/**
 * Wechselmarke (design/README §7, §9.2; Vertrag §4.1 Wechselband): Wechselt der Jagdzustand, während die App die
 * Drückjagd zeigt, wird die Statusleiste invertiert und an Stelle der Kontextzeile steht das Wechselband
 * „Neu: <Zustandsbegriff>“ mit „seit <hh:mm> · vor <n> min“ (bei n = 0 nur „seit <hh:mm>“, V-13) — mindestens
 * `dauer.wechselband` (5 min), danach bis zur ersten Berührung. Dazu einmal die Vibration des neuen Zustands (Android;
 * das iPhone vibriert nicht, LIM-0018).
 *
 * Maßgeblich ist der Wechsel, den dieses Gerät gesehen hat: Beim ersten Anzeigen einer Drückjagd gibt es keine Marke.
 * Der Stand gilt für die ganze App (Startansicht, Lagebild, …), nicht je Ansicht.
 */
interface Marke { zustand: string; seit: Date; ab: number; beruehrt: boolean }

const gesehen = new Map<string, string>();
const marken = new Map<string, Marke>();
const hoerer = new Set<() => void>();
let version = 0;

function melden(): void {
  version++;
  for (const h of hoerer) h();
}

function dauerMs(): number {
  const wert = getComputedStyle(document.documentElement).getPropertyValue("--dj-bewegung-dauer-wechselband").trim();
  const m = /^(\d+(?:\.\d+)?)(ms|s)$/.exec(wert);
  return m ? Number(m[1]) * (m[2] === "s" ? 1000 : 1) : 300_000;
}

if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", () => {
    let geaendert = false;
    for (const [dj, m] of marken) {
      if (Date.now() - m.ab >= dauerMs()) { marken.delete(dj); geaendert = true; }
    }
    if (geaendert) melden();
  }, { capture: true, passive: true });
}

/** Text des Wechselbands bzw. `null` ohne Marke. */
export function useWechselmarke(drueckjagdId: string, anzeige: Zustandsanzeige): { text: string } | null {
  useSyncExternalStore((h) => { hoerer.add(h); return () => hoerer.delete(h); }, () => version);
  const { bekannt, zustand, eingriff, jetzt } = anzeige;
  // erst nach dem ersten Abgleich mit der Serverzeit (ADR-0030): vorher rechnet das Gerät mit der zuletzt bekannten
  // Abweichung, und ein Abgleich, der den Zustand verschiebt, ist kein Wechsel der Jagd
  const abgeglichen = anzeige.kontakt.letzterAbgleich !== null;
  useEffect(() => {
    if (!bekannt || !abgeglichen) return;
    const vorher = gesehen.get(drueckjagdId);
    gesehen.set(drueckjagdId, zustand);
    if (vorher === undefined || vorher === zustand) return;
    // Zeitpunkt des Wechsels: der Eingriff, der ihn bewirkt hat, sonst der Augenblick, in dem das Gerät ihn sah (Zeitplan)
    const seit = eingriff && Math.abs(eingriff.zeitpunkt.getTime() - jetzt.getTime()) < 10 * 60_000 ? eingriff.zeitpunkt : jetzt;
    marken.set(drueckjagdId, { zustand, seit, ab: Date.now(), beruehrt: false });
    vibrieren(zustand === "Jagd im Gange" ? "jagd-im-gange" : "hahn-in-ruh");
    melden();
  }, [drueckjagdId, bekannt, abgeglichen, zustand, eingriff, jetzt]);
  const m = marken.get(drueckjagdId);
  if (!m || m.zustand !== zustand) return null;
  const minuten = Math.max(0, Math.floor((jetzt.getTime() - m.seit.getTime()) / 60_000));
  return { text: minuten === 0 ? `seit ${berlinUhrzeit(m.seit)}` : `seit ${berlinUhrzeit(m.seit)} · vor ${minuten} min` };
}
